import React, { useState } from 'react';
import { FilterState, FilterOptions, Committee } from '../../types/legislation';

interface LegislationFiltersProps {
  filters: FilterState;
  filterOptions: FilterOptions | null;
  sessions: string[];
  committees: Committee[];
  onFilterChange: (key: keyof FilterState, value: any) => void;
  onToggleStatus: (status: string) => void;
  onToggleType: (type: string) => void;
  onClear: () => void;
}

const LegislationFilters: React.FC<LegislationFiltersProps> = ({
  filters,
  filterOptions,
  sessions,
  committees,
  onFilterChange,
  onToggleStatus,
  onToggleType,
  onClear,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Common statuses to show as chips (most frequently used)
  const commonStatuses = [
    'Introduced',
    'Committee',
    'Adopted',
    'Enacted',
    'Filed',
    'Withdrawn',
  ];

  // Get statuses from API or use common defaults
  const availableStatuses = filterOptions?.statuses
    ?.filter((s) => s.MatterStatusActiveFlag === 1)
    .map((s) => s.MatterStatusName)
    .sort() || commonStatuses;

  // Curated type list - only the types that actually appear in our data
  const availableTypes = ['Introduction', 'Resolution'];

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="lg:hidden text-gray-500 hover:text-gray-700"
        >
          {isExpanded ? 'Hide' : 'Show'}
        </button>
      </div>

      <div className={`space-y-6 ${isExpanded ? '' : 'hidden lg:block'}`}>
        {/* Status Filter - Multi-select chips */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <div className="flex flex-wrap gap-2">
            {availableStatuses.slice(0, 10).map((status) => (
              <button
                key={status}
                onClick={() => onToggleStatus(status)}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  filters.statuses.includes(status)
                    ? 'bg-nyc-blue text-white border-nyc-blue'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-nyc-blue'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Type Filter - Multi-select dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
          <div className="flex flex-wrap gap-2">
            {availableTypes.map((type) => (
              <button
                key={type}
                onClick={() => onToggleType(type)}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  filters.types.includes(type)
                    ? 'bg-nyc-blue text-white border-nyc-blue'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-nyc-blue'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Committee Filter */}
        {committees.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Committee</label>
            <select
              value={filters.bodyId || ''}
              onChange={(e) => onFilterChange('bodyId', e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-nyc-blue focus:border-transparent"
            >
              <option value="">All Committees</option>
              {committees.map((committee) => (
                <option key={committee.id} value={committee.id}>
                  {committee.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Sponsor Filter */}
        {filterOptions?.persons && filterOptions.persons.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sponsor</label>
            <select
              value={filters.sponsors[0] || ''}
              onChange={(e) => onFilterChange('sponsors', e.target.value ? [Number(e.target.value)] : [])}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-nyc-blue focus:border-transparent"
            >
              <option value="">All Sponsors</option>
              {filterOptions.persons
                .filter(p => p.PersonActiveFlag === 1)
                .sort((a, b) => a.PersonFullName.localeCompare(b.PersonFullName))
                .map((person) => (
                  <option key={person.PersonId} value={person.PersonId}>
                    {person.PersonFullName}
                  </option>
                ))}
            </select>
          </div>
        )}

        {/* Date Range Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
          <div className="space-y-2">
            <select
              value={filters.dateField}
              onChange={(e) => onFilterChange('dateField', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-nyc-blue focus:border-transparent"
            >
              <option value="intro">Introduction Date</option>
              <option value="passed">Passed Date</option>
              <option value="lastAction">Last Action Date</option>
            </select>
            <div className="space-y-2">
              <input
                type="date"
                value={filters.dateFrom || ''}
                onChange={(e) => onFilterChange('dateFrom', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-nyc-blue focus:border-transparent"
                placeholder="From"
              />
              <input
                type="date"
                value={filters.dateTo || ''}
                onChange={(e) => onFilterChange('dateTo', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-nyc-blue focus:border-transparent"
                placeholder="To"
              />
            </div>
          </div>
        </div>

        {/* Session/Year Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Session/Year</label>
          <select
            value={filters.session || ''}
            onChange={(e) => onFilterChange('session', e.target.value || null)}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-nyc-blue focus:border-transparent"
          >
            <option value="">All Sessions</option>
            {sessions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Clear All Button */}
        <button
          onClick={onClear}
          className="w-full py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
        >
          Clear All Filters
        </button>
      </div>
    </div>
  );
};

export default LegislationFilters;
